export interface Circle {
  label: string;
  note?: string;
  image: string;
  href: string;
}

export const WAYS_TO_PLAY: Circle[] = [
  {
    label: "Men's Golf",
    note: 'Tournaments & leagues',
    image: 'https://readdy.ai/api/search-image?query=Male%20golfer%20mid%20follow-through%20on%20a%20sunny%20fairway%2C%20plain%20shirt%20with%20no%20logos%2C%20deep%20green%20blurred%20background%2C%20editorial%20sports%20portrait%2C%20warm%20natural%20light%2C%20high%20detail&width=600&height=600&seq=gs-circ-mens&orientation=squarish',
    href: '/#community',
  },
  {
    label: "Women's Golf",
    note: 'Coaching & series',
    image: 'https://readdy.ai/api/search-image?query=Confident%20woman%20golfer%20holding%20a%20club%20on%20a%20lush%20green%20course%2C%20plain%20apparel%20with%20no%20logos%2C%20soft%20green%20blurred%20background%2C%20editorial%20sports%20portrait%2C%20warm%20afternoon%20light%2C%20high%20detail&width=600&height=600&seq=gs-circ-womens&orientation=squarish',
    href: '/#community',
  },
  {
    label: 'Jr. Golf Hub',
    note: 'Growing the player',
    image: 'https://readdy.ai/api/search-image?query=Young%20junior%20golfer%20practicing%20a%20putt%20on%20a%20bright%20green%20with%20rolling%20hills%20behind%2C%20plain%20clothing%20with%20no%20logos%2C%20soft%20green%20background%2C%20editorial%20sports%20portrait%2C%20morning%20light%2C%20high%20detail&width=600&height=600&seq=gs-circ-junior&orientation=squarish',
    href: '/#community',
  },
  {
    label: 'Adaptive Golf',
    note: 'Directory & clinics',
    image: 'https://readdy.ai/api/search-image?query=Adaptive%20golfer%20lining%20up%20a%20shot%20on%20a%20lush%20green%20fairway%2C%20determined%20expression%2C%20plain%20clothing%20with%20no%20logos%2C%20soft%20green%20background%2C%20cinematic%20sports%20portrait%2C%20warm%20sunlight%2C%20high%20detail&width=600&height=600&seq=gs-circ-adaptive&orientation=squarish',
    href: '/#community',
  },
];

export const DISCOVER: Circle[] = [
  {
    label: 'Charity Spotlight',
    note: 'Play for a cause',
    image: 'https://readdy.ai/api/search-image?query=Golfers%20gathered%20on%20a%20clubhouse%20patio%20at%20golden%20hour%20after%20a%20charity%20round%2C%20warm%20tones%2C%20green%20course%20background%2C%20editorial%20photography%2C%20no%20logos%2C%20high%20detail&width=600&height=600&seq=gs-circ-charity&orientation=squarish',
    href: '/#news',
  },
  {
    label: 'Hall of Fame',
    note: 'Legends of the game',
    image: 'https://readdy.ai/api/search-image?query=Vintage%20golf%20trophy%20and%20clubs%20arranged%20on%20a%20wooden%20table%2C%20warm%20moody%20lighting%2C%20deep%20green%20background%2C%20editorial%20still%20life%20photography%2C%20no%20logos%2C%20high%20detail&width=600&height=600&seq=gs-circ-hof&orientation=squarish',
    href: '/#pros',
  },
  {
    label: 'Top 100 Courses',
    note: 'The bucket list',
    image: 'https://readdy.ai/api/search-image?query=Aerial%20view%20of%20a%20pristine%20winding%20golf%20course%20with%20sand%20bunkers%20and%20trees%2C%20golden%20hour%20light%2C%20deep%20green%20tones%2C%20editorial%20sports%20photography%2C%20no%20logos%2C%20high%20detail&width=600&height=600&seq=gs-circ-courses&orientation=squarish',
    href: '/#watch',
  },
  {
    label: 'Resources',
    note: 'Tips, gear & guides',
    image: 'https://readdy.ai/api/search-image?query=Golf%20gear%20layout%20with%20clubs%2C%20glove%20and%20balls%20on%20a%20neutral%20surface%2C%20clean%20studio%20lighting%2C%20warm%20neutral%20background%2C%20editorial%20product%20photography%2C%20no%20logos%2C%20high%20detail&width=600&height=600&seq=gs-circ-resources&orientation=squarish',
    href: '/#partners',
  },
];

interface Props {
  id: string;
  eyebrow: string;
  title: string;
  items: Circle[];
  tone?: 'light' | 'dark';
}

export default function CategoryCircles({ id, eyebrow, title, items, tone = 'light' }: Props) {
  const dark = tone === 'dark';
  return (
    <section
      id={id}
      className={`px-4 md:px-8 py-16 md:py-24 ${dark ? 'bg-foreground-950' : 'bg-background-50'}`}
    >
      <div className="text-center mb-10 md:mb-14">
        <p className={`font-label uppercase tracking-[0.28em] text-xs font-semibold ${dark ? 'text-accent-400' : 'text-primary-600'}`}>{eyebrow}</p>
        <h2 className={`font-heading font-bold text-4xl md:text-6xl leading-[1.02] mt-3 ${dark ? 'text-background-50' : 'text-foreground-950'}`}>{title}</h2>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-6 max-w-5xl mx-auto">
        {items.map((item) => (
          <a key={item.label} href={item.href} className="group flex flex-col items-center text-center cursor-pointer">
            <div className="w-32 h-32 md:w-44 md:h-44 rounded-full overflow-hidden border-4 border-transparent group-hover:border-accent-500 transition-colors">
              <img
                src={item.image}
                alt={item.label}
                title={`${item.label} — GreenSide News`}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
              />
            </div>
            <h3 className={`mt-5 font-heading font-bold text-xl md:text-2xl ${dark ? 'text-background-50 group-hover:text-accent-400' : 'text-foreground-950 group-hover:text-primary-600'} transition-colors`}>{item.label}</h3>
            {item.note && <p className={`mt-1 font-label uppercase tracking-widest text-[11px] ${dark ? 'text-background-400' : 'text-foreground-500'}`}>{item.note}</p>}
          </a>
        ))}
      </div>
    </section>
  );
}